(() => {
  if (window.EnsoHeaderRuntime) return;
  window.EnsoHeaderRuntime = true;

  const ROOT_SELECTOR = "[data-enso-header]";
  const OPEN_CLASS = "enso-header-open";
  const CLOSE_DURATION = 480;

  class EnsoHeader {
    constructor(root) {
      if (!root || root.dataset.enhanced === "true") return;

      this.root = root;
      this.disclosure = root.querySelector("[data-enso-header-disclosure]");
      this.summary = root.querySelector(".enso-header-trigger");
      this.sheet = root.querySelector("[data-enso-header-sheet]");
      this.backdrop = root.querySelector("[data-enso-header-close]");
      this.menuView = root.querySelector("[data-enso-menu-view]");
      this.searchView = root.querySelector("[data-enso-search-view]");
      this.openSearchLink = root.querySelector("[data-enso-open-search]");
      this.searchForm = root.querySelector("[data-enso-search-form]");
      this.searchInput = root.querySelector("[data-enso-search-input]");
      this.clearSearchButton = root.querySelector("[data-enso-search-clear]");
      this.searchResults = root.querySelector("[data-enso-search-results]");
      this.searchAll = root.querySelector("[data-enso-search-all]");
      this.searchStatus = root.querySelector("[data-enso-search-status]");
      this.masthead = root.querySelector(".enso-masthead");
      this.wordmark = root.querySelector(".enso-wordmark");
      this.scrollPosition = window.scrollY;
      this.protected = false;
      this.searchTimer = 0;
      this.searchSequence = 0;
      this.searchController = null;
      this.returnFocusOnClose = false;
      this.bodyWasLocked = false;
      this.bodyStyle = null;
      this.inertState = [];
      this.closeTimer = 0;
      this.destroyed = false;

      if (!this.disclosure || !this.summary || !this.sheet || !this.searchInput) return;

      this.onToggle = this.onToggle.bind(this);
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);
      this.onOpenSearch = this.onOpenSearch.bind(this);
      this.onClearSearch = this.onClearSearch.bind(this);
      this.onSearchInput = this.onSearchInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onScroll = this.onScroll.bind(this);

      root.dataset.enhanced = "true";
      root.dataset.view = "menu";
      root.dataset.hasResults = "false";
      this.summary.setAttribute("aria-expanded", String(this.disclosure.open));

      this.disclosure.addEventListener("toggle", this.onToggle);
      this.summary.addEventListener("click", this.onSummaryClick);
      this.backdrop?.addEventListener("click", this.onBackdropClick);
      this.openSearchLink?.addEventListener("click", this.onOpenSearch);
      this.clearSearchButton?.addEventListener("click", this.onClearSearch);
      this.searchInput.addEventListener("input", this.onSearchInput);
      document.addEventListener("keydown", this.onKeydown);
      document.addEventListener("focusin", this.onFocusIn);
      window.addEventListener("scroll", this.onScroll, { passive: true });

      this.updateProtected(true);
      this.syncOpenState();
      root._ensoHeader = this;
    }

    label(name, fallback = "") {
      return this.root.dataset[name] || fallback;
    }

    hasBlockingDialog() {
      return [...document.querySelectorAll("dialog[open]")].some(
        dialog => !this.root.contains(dialog)
      );
    }

    onSummaryClick(event) {
      if (this.root.dataset.closing === "true") {
        event.preventDefault();
        return;
      }
      if (this.disclosure.open) {
        event.preventDefault();
        this.close();
        return;
      }
      if (!this.disclosure.open && this.hasBlockingDialog()) {
        event.preventDefault();
      }
    }

    onToggle() {
      this.syncOpenState();
      if (!this.disclosure.open) delete this.root.dataset.closing;
    }

    syncOpenState() {
      const open = this.disclosure.open;
      this.root.dataset.open = String(open);
      this.summary.setAttribute("aria-expanded", String(open));
      this.summary.setAttribute(
        "aria-label",
        open
          ? this.label("closeNavigationLabel", "Close navigation")
          : this.label("openNavigationLabel", "Open navigation")
      );

      if (open) {
        this.sheet.setAttribute("aria-hidden", "false");
        this.root.setAttribute("role", "dialog");
        this.root.setAttribute("aria-modal", "true");
        this.root.setAttribute(
          "aria-label",
          this.label("navigationLabel", "Primary navigation")
        );
        document.querySelectorAll(`${ROOT_SELECTOR} [data-enso-header-disclosure][open]`).forEach(other => {
          if (other !== this.disclosure) other.open = false;
        });
        this.root.dataset.view = "menu";
        this.updateViews();
        this.lockPage();
      } else {
        this.sheet.setAttribute("aria-hidden", "true");
        this.root.removeAttribute("role");
        this.root.removeAttribute("aria-modal");
        this.root.removeAttribute("aria-label");
        this.abortSearch();
        this.root.dataset.view = "menu";
        this.updateViews();
        this.unlockPage();
        this.returnFocusOnClose = false;
        this.updateProtected(true);
      }
    }

    updateViews() {
      const search = this.root.dataset.view === "search";
      this.menuView?.setAttribute("aria-hidden", String(search));
      this.searchView?.setAttribute("aria-hidden", String(!search));
    }

    onBackdropClick() {
      this.close({ returnFocus: true });
    }

    close({ returnFocus = false } = {}) {
      if (!this.disclosure.open || this.root.dataset.closing === "true") return;
      this.returnFocusOnClose = returnFocus;
      if (returnFocus || this.sheet.contains(document.activeElement)) {
        this.summary.focus({ preventScroll: true });
        this.returnFocusOnClose = false;
      }
      this.sheet.setAttribute("aria-hidden", "true");
      this.root.dataset.closing = "true";
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.closeTimer = window.setTimeout(() => {
        this.closeTimer = 0;
        this.disclosure.open = false;
      }, reducedMotion ? 0 : CLOSE_DURATION);
    }

    onOpenSearch(event) {
      event.preventDefault();
      this.root.dataset.view = "search";
      this.searchView?.setAttribute("aria-hidden", "false");
      this.searchInput.focus({ preventScroll: true });
      this.menuView?.setAttribute("aria-hidden", "true");
    }

    onClearSearch() {
      this.searchInput.value = "";
      this.resetSearch();
      requestAnimationFrame(() => this.searchInput.focus({ preventScroll: true }));
    }

    onSearchInput() {
      window.clearTimeout(this.searchTimer);
      const query = this.searchInput.value.trim();
      if (query.length < 2) {
        this.resetSearch();
        return;
      }

      this.searchStatus.textContent = this.label("searching", "Searching…");
      this.searchResults.hidden = true;
      this.searchAll.hidden = true;
      this.root.dataset.hasResults = "false";
      this.searchTimer = window.setTimeout(() => this.search(query), 180);
    }

    abortSearch() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = 0;
      this.searchSequence += 1;
      this.searchController?.abort();
      this.searchController = null;
    }

    resetSearch() {
      this.abortSearch();
      this.searchResults.replaceChildren();
      this.searchResults.hidden = true;
      this.searchAll.hidden = true;
      this.root.dataset.hasResults = "false";
      this.searchStatus.textContent = this.label(
        "searchEmpty",
        "Enter an object or material."
      );
    }

    async search(query) {
      const sequence = ++this.searchSequence;
      this.searchController?.abort();
      this.searchController = new AbortController();

      try {
        const endpoint = new URL(
          this.root.dataset.searchUrl || "/search/suggest.json",
          window.location.origin
        );
        if (!endpoint.pathname.endsWith(".json")) {
          endpoint.pathname = `${endpoint.pathname.replace(/\/$/, "")}.json`;
        }
        endpoint.searchParams.set("q", query);
        endpoint.searchParams.set("resources[type]", "product");
        endpoint.searchParams.set("resources[limit]", "1");
        endpoint.searchParams.set(
          "resources[options][unavailable_products]",
          "last"
        );

        const response = await fetch(endpoint, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          signal: this.searchController.signal
        });
        if (!response.ok) throw new Error(`Predictive search ${response.status}`);

        const payload = await response.json();
        if (sequence !== this.searchSequence) return;
        const products = payload?.resources?.results?.products || [];
        this.renderSearch(query, products.slice(0, 1));
      } catch (error) {
        if (error.name === "AbortError" || sequence !== this.searchSequence) return;
        this.searchResults.replaceChildren();
        this.searchResults.hidden = true;
        this.searchAll.hidden = true;
        this.root.dataset.hasResults = "false";
        this.searchStatus.textContent = this.label(
          "searchError",
          "Search is temporarily unavailable."
        );
      }
    }

    renderSearch(query, products) {
      this.searchResults.replaceChildren();
      this.root.dataset.hasResults = String(products.length > 0);

      if (!products.length) {
        this.searchResults.hidden = true;
        this.searchAll.hidden = true;
        this.searchStatus.textContent = this.label("searchNone", "No objects found.");
        return;
      }

      products.forEach(product => {
        const link = document.createElement("a");
        link.className = "enso-header-search__result";
        link.href = this.internalUrl(product.url, "/search");

        const imageCandidate =
          product.image?.url ||
          product.image ||
          product.featured_image?.url ||
          product.featured_image ||
          "";
        const imageUrl = typeof imageCandidate === "string" ? imageCandidate : "";
        if (imageUrl) {
          const image = document.createElement("img");
          image.src = this.assetUrl(imageUrl);
          image.alt = "";
          image.loading = "eager";
          image.decoding = "async";
          link.append(image);
        } else {
          const imagePlaceholder = document.createElement("span");
          imagePlaceholder.className = "enso-header-search__image-placeholder";
          imagePlaceholder.setAttribute("aria-hidden", "true");
          link.append(imagePlaceholder);
        }

        const copy = document.createElement("span");
        copy.className = "enso-header-search__result-copy";
        const title = document.createElement("strong");
        title.textContent = this.productTitle(product);
        const data = document.createElement("span");
        data.textContent = this.productData(product);
        copy.append(title, data);
        link.append(copy);
        this.searchResults.append(link);
      });

      const searchUrl = new URL(
        this.root.dataset.searchPageUrl || "/search",
        window.location.origin
      );
      searchUrl.searchParams.set("q", query);
      this.searchAll.href = `${searchUrl.pathname}${searchUrl.search}`;
      this.searchResults.hidden = false;
      this.searchAll.hidden = false;
      this.searchStatus.textContent = `${products.length} ${this.label(
        "resultPrefix",
        "result"
      )}`;
    }

    productData(product) {
      const parts = [];
      if (product.type) {
        const type = String(product.type).trim();
        parts.push(
          type === type.toUpperCase()
            ? type.toLowerCase().replace(/(^|\s)\S/g, character => character.toUpperCase())
            : type
        );
      }
      const rawPrice = product.price_min ?? product.price;
      const numericPrice = Number(rawPrice);
      if (Number.isFinite(numericPrice)) {
        const currency = this.root.dataset.currency || "AED";
        const amount = numericPrice;
        let money = `${currency} ${amount.toLocaleString(this.root.dataset.locale || "en")}`;
        try {
          money = new Intl.NumberFormat(this.root.dataset.locale || "en", {
            style: "currency",
            currency,
            currencyDisplay: "code",
            minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2
          }).format(amount);
        } catch (_) {
          // The deterministic currency fallback above remains customer-readable.
        }
        parts.push(`${this.label("pricePrefix", "From")} ${money.replace(/\s+/g, " ")}`);
      }
      return parts.join(" · ");
    }

    productTitle(product) {
      const title = String(product.title || "").trim();
      const type = String(product.type || "").trim();
      if (!title || !type) return title;
      const escapedType = type.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const suffix = new RegExp(`\\s+${escapedType}$`, "i");
      return title.replace(suffix, "") || title;
    }

    internalUrl(value, fallback) {
      try {
        const url = new URL(value || fallback, window.location.origin);
        if (url.origin !== window.location.origin) return fallback;
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (_) {
        return fallback;
      }
    }

    assetUrl(value) {
      if (typeof value !== "string") return "";
      if (value.startsWith("//")) return `${window.location.protocol}${value}`;
      return value;
    }

    getFocusables() {
      const activeView =
        this.root.dataset.view === "search" ? this.searchView : this.menuView;
      const panelControls = [...activeView.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter(element => {
        const style = window.getComputedStyle(element);
        return style.visibility !== "hidden" && style.display !== "none";
      });
      return [this.wordmark, this.summary, ...panelControls].filter(Boolean);
    }

    onKeydown(event) {
      if (!this.disclosure.open) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.close({ returnFocus: true });
        return;
      }
      if (event.key !== "Tab") return;

      const focusables = this.getFocusables();
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onFocusIn(event) {
      if (!this.disclosure.open) return;
      const focusables = this.getFocusables();
      if (this.root.contains(event.target)) return;
      (this.summary || focusables[0])?.focus({ preventScroll: true });
    }

    lockPage() {
      if (document.documentElement.classList.contains(OPEN_CLASS)) return;
      this.scrollPosition = window.scrollY;
      document.documentElement.classList.add(OPEN_CLASS);

      this.bodyStyle = {
        position: document.body.style.position,
        top: document.body.style.top,
        right: document.body.style.right,
        left: document.body.style.left,
        width: document.body.style.width
      };
      document.body.style.position = "fixed";
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.right = "0";
      document.body.style.left = "0";
      document.body.style.width = "100%";
      this.bodyWasLocked = true;

      this.inertState = [...document.querySelectorAll("#MainContent, .enso-footer")].map(
        element => ({
          element,
          inert: element.inert,
          ariaHidden: element.getAttribute("aria-hidden")
        })
      );
      this.inertState.forEach(({ element }) => {
        element.inert = true;
        element.setAttribute("aria-hidden", "true");
      });
    }

    unlockPage() {
      if (!document.documentElement.classList.contains(OPEN_CLASS)) return;
      document.documentElement.classList.remove(OPEN_CLASS);

      this.inertState.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
      });
      this.inertState = [];

      if (this.bodyWasLocked && this.bodyStyle) {
        Object.assign(document.body.style, this.bodyStyle);
        this.bodyWasLocked = false;
        this.bodyStyle = null;
        const html = document.documentElement;
        const previousScrollBehavior = html.style.scrollBehavior;
        html.style.scrollBehavior = "auto";
        window.scrollTo({ top: this.scrollPosition, left: 0, behavior: "auto" });
        requestAnimationFrame(() => {
          html.style.scrollBehavior = previousScrollBehavior;
        });
      }
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateProtected();
      });
    }

    updateProtected(force = false) {
      const y = this.bodyWasLocked ? this.scrollPosition : window.scrollY;
      const next = this.protected ? y > 8 : y >= 24;
      if (!force && next === this.protected) return;
      this.protected = next;
      this.root.dataset.protected = String(next);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.abortSearch();
      window.clearTimeout(this.closeTimer);
      this.closeTimer = 0;
      delete this.root.dataset.closing;
      this.disclosure.removeEventListener("toggle", this.onToggle);
      this.summary.removeEventListener("click", this.onSummaryClick);
      this.backdrop?.removeEventListener("click", this.onBackdropClick);
      this.openSearchLink?.removeEventListener("click", this.onOpenSearch);
      this.clearSearchButton?.removeEventListener("click", this.onClearSearch);
      this.searchInput.removeEventListener("input", this.onSearchInput);
      document.removeEventListener("keydown", this.onKeydown);
      document.removeEventListener("focusin", this.onFocusIn);
      window.removeEventListener("scroll", this.onScroll);
      if (this.disclosure.open) {
        this.disclosure.open = false;
        this.unlockPage();
      }
      delete this.root._ensoHeader;
    }
  }

  function initialize(scope = document) {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(root => roots.push(root));
    roots.forEach(root => new EnsoHeader(root));
  }

  initialize();
  document.addEventListener("shopify:section:load", event => initialize(event.target));
  document.addEventListener("shopify:section:unload", event => {
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(root => root._ensoHeader?.destroy());
  });
})();
